import { ThreeDSecureVerifyPayload } from 'braintree-web';
import React, { useCallback, useEffect } from 'react';

import { useTracking } from '@hooks/useTracking';
import { useTranslation } from '@hooks/useTranslations';
import { useConfig } from '@providers/ConfigProvider';
import { useErrorContext } from '@providers/ErrorContextProvider';
import { authorizePayment } from '@services/braintreeService';
import { Additional3dsInfo, CreditCardType, FraudPreventionRecommendation } from '@types-internal';

export type BraintreeAuthorizationResult = {
  nonce: string;
  liabilityShifted: boolean;
  liabilityShiftPossible: boolean;
  status: string;
  deviceData: string;
};

export type BraintreeAuthorizerProps = {
  token: string;
  bin: string;
  nonce: string;
  amount: number;
  currency: string;
  additional3dsInfo?: Additional3dsInfo;
  cardType: CreditCardType;
  isSubscriptionFirst: boolean;
  subscriptionFirstInterval: string;
  customerId: string;
  customerType: string;
  sid: string;
  fraudPreventionRecommendation: FraudPreventionRecommendation;
  siteId: number;
};

// Override since typescript types for braintree are not inline with actual implementation ...
export type ThreeDSecureVerifyPayloadOverride = Omit<
  ThreeDSecureVerifyPayload,
  'details' | 'threeDSecureInfo'
> & {
  details: {
    cardType: string;
    lastTwo: string;
    lastFour: string;
    expirationYear: string;
    expirationMonth: string;
    cardholderName?: string;
    bin: string;
  };
  threeDSecureInfo: {
    liabilityShiftPossible: boolean;
    liabilityShifted: boolean;
    cavv: string;
    xid: string;
    dsTransactionId: string;
    threeDSecureVersion: string;
    eciFlag: string;
    acsUrl: string | null;
    threeDSecureAuthenticationId: string;
    status: string;
  };
};

const BraintreeAuthorizer: React.FC<BraintreeAuthorizerProps> = ({
  token,
  nonce,
  bin,
  amount,
  additional3dsInfo,
  cardType,
  isSubscriptionFirst,
  subscriptionFirstInterval,
  customerId,
  customerType,
  sid,
  currency,
  fraudPreventionRecommendation,
  siteId,
}) => {
  const { t } = useTranslation();
  const { setError } = useErrorContext();
  const { clientId } = useConfig().config.optimizely;
  const track = useTracking({
    sid,
    siteId,
    currency,
    customerId,
    guest: customerType === 'GUEST_CUSTOMER',
  });

  const handleAuthorize = useCallback(async () => {
    const result = await authorizePayment({
      t,
      token,
      bin,
      nonce,
      amount,
      additional3dsInfo,
      cardType,
      isSubscriptionFirst,
      subscriptionFirstInterval,
      customerId,
      fraudPreventionRecommendation,
      siteId,
      optimizelyId: clientId as string,
      track,
    });

    if (!result.success) {
      setError({ message: result.errorMessage, retryable: result.retryable });
      throw new Error(result.errorMessage);
    }
    return result;
  }, [
    t,
    setError,
    token,
    bin,
    nonce,
    amount,
    additional3dsInfo,
    cardType,
    isSubscriptionFirst,
    subscriptionFirstInterval,
  ]);

  useEffect(() => {
    (window as any).Payment = {
      ...((window as any).Payment || {}),
      braintreeAuthorize: handleAuthorize,
    };
  }, [handleAuthorize]);

  return null;
};

export default BraintreeAuthorizer;
